import { Flex } from 'antd';
import { Formik } from 'formik';
import { useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { loginApi } from '../../api/auth-api';
import { Button, Input } from '../../common';
import { userType, getErrorString } from '../../utils';
import { BASE_PATH, LEADS_PATH } from '../../constants/admin-paths';
import { removeUser, setUser } from '../../reducers/user-slice';
import { setCookie } from '../../utils/cookies-util';
import AuthWrapper from './authWrapper';
import { fetchCurrentTenant } from 'reducers/tenant-config-slice';
import { regex } from 'constants/generic-regex';

export default function Signin({ handleLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const formikRef = useRef();

  const fromPage = (role) =>
    location.state?.from?.pathname &&
    location.state.from.pathname !== '/' &&
    location.state?.from?.pathname?.includes(userType())
      ? location.state.from.pathname
      : role === 'dashboard_viewer'
        ? BASE_PATH
        : LEADS_PATH;

  useEffect(() => {
    dispatch(removeUser());
  }, []);

  const getValidationSchema = () =>
    yup.object().shape({
      email: yup.string().required('Email is required').email('Valid Email is required'),
      password: yup
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(64, 'Password must not exceed 64 characters')
        .matches(regex.password, 'Password must contain only letters, numbers, and symbols. Spaces are not allowed.')
        .required('Password is required'),
    });

  const getInitialValues = () => ({
    email: '',
    password: '',
  });

  const onSubmit = async (values, { setSubmitting, setStatus }) => {
    setSubmitting(true);
    try {
      const data = { user: { email: values?.email || '', password: values?.password || '' } };
      const res = await loginApi(data);
      if (res && res?.status) {
        dispatch(setUser(res?.data?.data ?? {}));
        dispatch(fetchCurrentTenant(window.location.hostname));
        setCookie('access-token', res.headers['authorization']);
        handleLogin();
        navigate(fromPage(res?.data?.data?.roles?.[0]?.name), { replace: true });
        return;
      } else {
        toast.error(res?.message ?? 'Login Failed');
        setStatus({ message: res?.message });
        return;
      }
    } catch (error) {
      toast.error(getErrorString(error));
      setStatus({ message: getErrorString(error) });
    } finally {
      setSubmitting(false);
    }
  };
  const handleEmailChange = (e, setFieldValue, handleChange) => {
    const value = e.target.value;

    if (value === '') {
      setFieldValue('email', '');
      return;
    }

    handleChange(e);
  };

  return (
    <AuthWrapper>
      <Formik
        innerRef={formikRef}
        initialValues={getInitialValues()}
        validationSchema={getValidationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          status,
          setStatus,
        }) => (
          <form className="form-wrap" autoComplete="on" onSubmit={handleSubmit}>
            <Flex align="center" justify="center" vertical gap={8}>
              <h3 className="text-align-center heading-text">Admin Portal - Secure Sign-In</h3>
              <h5 style={{ maxWidth: '414px' }} className="text-align-center subheading-text">
                Access your dashboard and manage settings with ease and security.
              </h5>
            </Flex>
            <Flex vertical gap={16}>
              <Input
                placeholder="e.g username@gmail.com"
                name="email"
                autoComplete="username"
                className={'h5-regular input-box'}
                onChange={(e) => handleEmailChange(e, setFieldValue, handleChange)}
                onBlur={handleBlur}
                value={(values?.email || '')?.replace(/\s/g, '')}
                errorMsg={errors.email && touched.email && errors.email}
              />
              <Input
                isPassword={true}
                placeholder="Password"
                name="password"
                autoComplete="current-password"
                className={'h5-regular input-box'}
                onChange={handleChange}
                onBlur={handleBlur}
                onPressEnter={handleSubmit}
                value={values?.password}
                errorMsg={errors.password && touched.password && errors.password}
              />
            </Flex>
            <Flex vertical gap={16}>
              <Button
                text={'Sign In'}
                type="primary"
                className={'h5-semibold primary-button'}
                size="large"
                onClick={handleSubmit}
                loading={isSubmitting}
              />
              {/*
                <>
                <Divider style={{ color: 'var(--user-neutral-7)' }} className="h5-medium" plain>
                  Or login with
                </Divider>
                <GoogleSignIn handleLogin={handleLogin} fromPage={fromPage} text={'Sign in with Google'} />
                </>
                */}
            </Flex>
          </form>
        )}
      </Formik>
    </AuthWrapper>
  );
}
